import React, { useRef, useState } from 'react';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';

interface DragAndDropFileUploaderProps {
  maxSize?: number;
  onFileUpload: (files: File[]) => void;
  accept?: string[];
  multiple?: boolean;
}

const DragAndDropFileUploader: React.FC<DragAndDropFileUploaderProps> = ({
  maxSize = 10,
  onFileUpload,
  accept,
  multiple = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(multiple ? Array.from(e.dataTransfer.files) : [e.dataTransfer.files[0]]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(multiple ? Array.from(e.target.files) : [e.target.files[0]]);
    }
  };

  const handleFiles = (files: File[]) => {
    const validFiles = files.filter((file) => {
      if (accept && accept.length > 0) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!accept.includes(fileExtension || '')) {
          return false;
        }
      }
      return file.size <= maxSize * 1024 * 1024;
    });

    if (validFiles.length > 0) {
      onFileUpload(validFiles);
    }
    if (validFiles.length !== files.length) {
      SystemNotification.info(`Tipo de arquivo inválido ou tamanho máximo de ${maxSize}MB excedido.`);
    }
  };

  const onButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Don't re-open the picker when the click originates from the file input itself.
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div
      className={`h-100 d-flex flex-column justify-content-center align-items-center border-2 border-dashed rounded-lg pt-10 px-4 text-center cursor-pointer border-primary
                  ${dragActive ? 'border-primary bg-primary bg-opacity-10' : 'border-blue-500'}`}
      onClick={onButtonClick}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <svg
        className="bi bi-cloud-arrow-up text-primary"
        fill="currentColor"
        height="32"
        viewBox="0 0 16 16"
        width="32"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.646 5.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 6.707V10.5a.5.5 0 0 1-1 0V6.707L6.354 7.854a.5.5 0 1 1-.708-.708z"
          fillRule="evenodd"
        />
        <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773 16 11.569 14.502 13 12.687 13H3.781C1.708 13 0 11.366 0 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383m.653.757c-.757.653-1.153 1.44-1.153 2.056v.448l-.445.049C2.064 6.805 1 7.952 1 9.318 1 10.785 2.23 12 3.781 12h8.906C13.98 12 15 10.988 15 9.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 4.825 10.328 3 8 3a4.53 4.53 0 0 0-2.941 1.1z" />
      </svg>
      <p className="mb-1">
        Arraste seu arquivo ou <span className="text-primary">busque</span>
      </p>
      <p className="text-sm text-muted">Máximo {maxSize} MB por arquivo</p>
      <input
        ref={inputRef}
        accept={accept ? accept.map((type) => `.${type}`).join(',') : undefined}
        className="hidden"
        multiple={multiple}
        style={{ display: 'none' }}
        type="file"
        onChange={handleChange}
      />
    </div>
  );
};

export default DragAndDropFileUploader;
